import Track from 'core/logger/track';
import { User, Provider } from '../models/store';
import {
  CLICK_SIGN_OUT_ALL_SESSIONS,
  SESSIONS_DATA_EMPTY,
  SIGN_OUT_ALL_SESSIONS,
  SYSTEM_EMAIL_VERIFY_FAIL,
} from '../models/names';

const sectionMapping = {
  personalize: 'PersonalizeQuestions',
  welcome: 'WelcomeAfterRegistration',
};

class AuthTracker {
  setUserDataInAmp = (user: User) => {
    const authProvider =
      user.providerData && user.providerData[0] && user.providerData[0].providerId
        ? user.providerData[0].providerId
        : '';
    Track.getInstance().ampSetUserProperties({
      emailSubscriptionStatus: user.emailSubscriptionStatus,
      emailVerified: user.emailVerified,
      authProvider,
      paymentMethod: user.paymentMethod || '',
      planLabel: user.planLabel || '',
      quillbotUid: user.uid,
    });
  };

  setUserId = (email: string | null | undefined, quId: string | null | undefined = null) => {
    Track.getInstance().setUserId(email || null, quId);
  };

  setUserStatus = (status: 'guest' | 'user' | 'premium') => {
    if (window && window.ga) window.ga('set', 'dimension1', status);
  };

  forcedLogout = (uid: string, status: string | number) => {
    Track.getInstance().storeActionEvent('forcedLogout', { uid, status });
  };

  authentication = (
    eventName: 'signupSuccess' | 'loginSuccess' | 'logoutSuccess',
    provider?: Provider,
    triggeredBy?: string
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: Record<string, any> = {};
    if (provider) data.method = provider;
    if (triggeredBy) data.triggeredBy = triggeredBy;
    Track.getInstance().storeActionEvent(eventName, data);
    Track.getInstance().ampEvent(eventName, data);
    Track.getInstance().gTagEvent(eventName, data);
  };

  clickSignOutAllSessions = () => {
    Track.getInstance().storeActionEvent(CLICK_SIGN_OUT_ALL_SESSIONS);
    Track.getInstance().ampEvent(CLICK_SIGN_OUT_ALL_SESSIONS);
  };

  sessionDataEmpty = () => {
    Track.getInstance().storeActionEvent(SESSIONS_DATA_EMPTY);
    Track.getInstance().ampEvent(SESSIONS_DATA_EMPTY);
  };

  signOutAllSessions = (data: { success: boolean }) => {
    Track.getInstance().storeActionEvent(SIGN_OUT_ALL_SESSIONS, data);
    Track.getInstance().ampEvent(SIGN_OUT_ALL_SESSIONS, data);
  };

  skippedPersonalizeSection = (pathName: string) => {
    const eventName = 'skippedPersonalizeSection';
    const data = {
      flow: pathName,
    };
    Track.getInstance().storeActionEvent(eventName, data);
    Track.getInstance().ampEvent(eventName, data);
  };

  pageLoaded = (route: string, referrer?: string) => {
    Track.getInstance().visitedPage(route, referrer);
  };

  shownSection = (section: keyof typeof sectionMapping) => {
    if (sectionMapping[section]) {
      const eventName = `shown${sectionMapping[section]}`;
      Track.getInstance().storeActionEvent(eventName);
      Track.getInstance().ampEvent(eventName);
    }
  };

  // same function is being used in misc tracking
  extensionLinkClicked = (extensionName: 'word' | 'chrome' | 'docs', position: string) => {
    const eventName = 'clickedExtensionLink';
    const data = {
      extensionName,
      position,
    };
    Track.getInstance().storeActionEvent(eventName, data);
    Track.getInstance().ampEvent(eventName, data);
  };

  openedMainMenu = () => {
    const eventName = 'openedMainMenu';
    Track.getInstance().storeActionEvent(eventName);
    Track.getInstance().ampEvent(eventName);
  };

  shownOneTap = () => {
    const eventName = 'shownOneTap';
    Track.getInstance().storeActionEvent(eventName);
    Track.getInstance().ampEvent(eventName);
  };

  verifyEmailFailed = () => {
    Track.getInstance().storeActionEvent(SYSTEM_EMAIL_VERIFY_FAIL);
    Track.getInstance().ampEvent(SYSTEM_EMAIL_VERIFY_FAIL);
  };
}
// @Todo: remove below export implementation and add static getinstance from class itself
const authTracker = new AuthTracker();

export default authTracker;
export { authTracker };
